import { onValue, ref } from 'firebase/database';
import { db } from '@/core/firebase/config';
import { useEffect, useState } from 'react';
import { getEnvDbPrefix } from '@/core/firebase/config';
import useGetOfficeList from '@/features/office/hooks/useGetOfficeList';
import useSavePreferredOffice from './useSavePreferredOffice';

export default function useGetPreferredOffice(userId: string | null | undefined) {
  const [preferredOffice, setPreferredOffice] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const dbStringRef = getEnvDbPrefix(`user/${userId}/officeId`);
  const officeList = useGetOfficeList(Boolean(userId));
  const savePreferredOffice = useSavePreferredOffice();

  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const subscription = onValue(
      ref(db, dbStringRef),
      snapshot => {
        const data = snapshot.val() as string;
        setPreferredOffice(data || '');
        setIsLoading(false);
        setHasInitialized(true);
      },
      errorObject => {
        console.error('The read failed: ' + errorObject);
        setIsLoading(false);
        setHasInitialized(true);
      }
    );
    return () => subscription();
  }, [userId, dbStringRef]);

  // More controlled fallback logic - only run after data is loaded and initialized
  useEffect(() => {
    // Only run fallback logic if:
    // 1. We have a userId
    // 2. Data has been initialized (not still loading)
    // 3. We have office list data
    // 4. Either no preferred office is set, or the preferred office is invalid
    if (!userId || !hasInitialized || isLoading || !officeList || officeList.length === 0) {
      return;
    }

    const enabledOffices = officeList.filter(office => !office.disabled);

    // If no preferred office is set and we have enabled offices, set the first one
    if (!preferredOffice && enabledOffices.length > 0) {
      const firstEnabledOffice = enabledOffices[0];
      savePreferredOffice(userId, firstEnabledOffice.id);
      setPreferredOffice(firstEnabledOffice.id);
      return;
    }

    // If preferred office exists, check if it's still valid
    if (preferredOffice) {
      const selectedOffice = officeList.find(office => office.id === preferredOffice);

      // Only fallback if the office is disabled or doesn't exist anymore
      if ((selectedOffice?.disabled || !selectedOffice) && enabledOffices.length > 0) {
        const firstEnabledOffice = enabledOffices[0];
        savePreferredOffice(userId, firstEnabledOffice.id);
        setPreferredOffice(firstEnabledOffice.id);
      }
    }
  }, [preferredOffice, officeList, userId, savePreferredOffice, hasInitialized, isLoading]);

  return { preferredOffice, isLoading };
}
